import { useParams } from "react-router";
import { useTranslations } from "@acto/web-common/locales";
import {
  ChartBars,
  ChartBarsOutline,
  ChatBubble,
  PieChart,
  PieChartFilled,
  Receipt,
  ReceiptFilled,
} from "@acto/web-common/components";
import { Kpi } from "@acto/taskpane/core/domain";
import type { PeriodSelection } from "@acto/taskpane/field-sales-2/utils/periodSelection";
import { useOpenCustomerChat } from "@acto/taskpane/field-sales-2/utils/useOpenCustomerChat";
import { useProductDrill } from "@acto/taskpane/field-sales-2/pages/analysis/ProductDrillContext";
import {
  BottomNavCircleButton,
  BottomNavShell,
  BottomNavTabBar,
  BottomNavTabRow,
  type BottomNavTab,
} from "./components/patterns/BottomNav";
import { PeriodPicker } from "./components/primitives/PeriodPicker";

interface AnalysisBottomNavProps {
  selectedKpi: Kpi;
  onKpiChange: (kpi: Kpi) => void;
  period?: {
    selection: PeriodSelection;
    onSelectionChange: (selection: PeriodSelection) => void;
  };
}

export function AnalysisBottomNav({ selectedKpi, onKpiChange, period }: AnalysisBottomNavProps) {
  const tKpi = useTranslations("kpi");
  const tNav = useTranslations("fieldSalesTwo.bottomNav");
  const { id } = useParams<{ id: string }>();
  const openChat = useOpenCustomerChat(id);
  const { stack, drillTo } = useProductDrill();

  const leaveProductLevel = () => {
    if (stack.at(-1)?.kind === "product") drillTo(stack.length - 1);
  };

  const switchKpi = (kpi: Kpi) => {
    if (kpi !== selectedKpi) leaveProductLevel();
    onKpiChange(kpi);
  };

  const tabs: BottomNavTab[] = [
    {
      key: "revenue",
      label: tKpi("revenueTab"),
      icon: <ChartBarsOutline width={26} height={26} aria-hidden="true" />,
      activeIcon: <ChartBars width={26} height={26} aria-hidden="true" />,
      onClick: () => switchKpi(Kpi.REVENUE),
      isActive: selectedKpi === Kpi.REVENUE,
    },
    {
      key: "margin",
      label: tKpi("marginTab"),
      icon: <PieChart width={26} height={26} aria-hidden="true" />,
      activeIcon: <PieChartFilled width={26} height={26} aria-hidden="true" />,
      onClick: () => switchKpi(Kpi.MARGIN),
      isActive: selectedKpi === Kpi.MARGIN,
    },
    {
      key: "orders",
      label: tKpi("ordersTab"),
      icon: <Receipt width={26} height={26} aria-hidden="true" />,
      activeIcon: <ReceiptFilled width={26} height={26} aria-hidden="true" />,
      onClick: () => switchKpi(Kpi.ORDERS),
      isActive: selectedKpi === Kpi.ORDERS,
    },
  ];

  return (
    <BottomNavShell>
      {period && <PeriodPicker selection={period.selection} onSelectionChange={period.onSelectionChange} />}
      <BottomNavTabRow>
        <BottomNavTabBar tabs={tabs} />
        <BottomNavCircleButton
          icon={<ChatBubble width={24} height={24} aria-hidden="true" />}
          onClick={openChat}
          aria-label={tNav("openChat")}
        />
      </BottomNavTabRow>
    </BottomNavShell>
  );
}
